import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { Controller, ControllerRenderProps } from 'react-hook-form'

import toastStore from '../../../stores/toast-store'

import { StyledImageBufferField } from '../../styled'
import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'

export type TImageBufferField<T> = TCommonFields<T> & {
    type: 'image-buffer'
    width: string
    height: string
    maxSize?: number // in MB
}

type TImageBufferFieldProps<T> = {
    field: ControllerRenderProps<any, string>
    field_: TImageBufferField<T>
    disabled?: boolean
}

type TSerializedBuffer = { type: 'Buffer'; data: number[] }

const isSerializedBuffer = (value: unknown): value is TSerializedBuffer =>
    !!value &&
    typeof value === 'object' &&
    (value as TSerializedBuffer).type === 'Buffer' &&
    Array.isArray((value as TSerializedBuffer).data)

// Browser-safe base64 (the Node `Buffer` global is not guaranteed on the client).
const bytesToBase64 = (bytes: Uint8Array) => {
    let binary = ''
    const chunkSize = 0x8000
    for (let i = 0; i < bytes.length; i += chunkSize)
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))

    return btoa(binary)
}

export const getImageBufferInputId = (name: string) => `${name}-file`

export const ImageBufferField = <T,>({ field_, field, disabled = field.disabled }: TImageBufferFieldProps<T>) => {
    'use no memo'
    const { value, onChange, ref, name, onBlur } = field
    const { updateToast } = toastStore
    const t = useTranslations('form.image-buffer-field')

    // preview of a buffer that came from the backend
    const [bufferSrc, setBufferSrc] = useState<string | undefined>(undefined)
    useEffect(() => {
        if (!isSerializedBuffer(value)) {
            setBufferSrc(undefined)
            return
        }

        let cancelled = false
        const bytes = new Uint8Array(value.data)
        const detect = async () => {
            const { fileTypeFromBuffer } = await import('file-type')
            const fileType = await fileTypeFromBuffer(bytes)
            if (!cancelled)
                setBufferSrc(fileType ? `data:${fileType.mime};base64,${bytesToBase64(bytes)}` : undefined)
        }

        detect()
        return () => {
            cancelled = true
        }
    }, [value])

    // preview of a freshly picked file; the object URL is released when the file changes or on unmount
    const [fileSrc, setFileSrc] = useState<string | undefined>(undefined)
    useEffect(() => {
        if (!(value instanceof Blob)) {
            setFileSrc(undefined)
            return
        }

        const url = URL.createObjectURL(value)
        setFileSrc(url)
        return () => URL.revokeObjectURL(url)
    }, [value])

    const src = fileSrc ?? bufferSrc
    const inputId = getImageBufferInputId(name)

    return (
        <StyledImageBufferField>
            <div
                style={{
                    width: field_.width,
                    height: field_.height,
                }}
            >
                {src && <Image src={src} fill alt="" unoptimized />}
            </div>

            <label htmlFor={inputId}>
                {t('select-file')}
                <input
                    ref={ref}
                    name={name}
                    onBlur={onBlur}
                    disabled={disabled}
                    id={inputId}
                    type="file"
                    accept="image/*"
                    onChange={event => {
                        const file = event.target.files ? event.target.files[0] : null
                        if (file && field_.maxSize && file.size > field_.maxSize * 1024 * 1024) {
                            updateToast({
                                type: 'error',
                                message: t('too-big-image', {
                                    size: (file.size / 1024 / 1024).toFixed(2),
                                    maxSize: field_.maxSize,
                                }),
                            })

                            event.target.value = ''
                            return
                        }

                        onChange(file)
                        field_.onChange?.(file)
                    }}
                />
            </label>
        </StyledImageBufferField>
    )
}

type TProps<T> = {
    rowField: TImageBufferField<T>
    fieldName: string
    labelWidth?: string
}

const ImageBufferFieldDefault = <T,>({ rowField, fieldName, labelWidth }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-image-buffer"
            htmlFor={getImageBufferInputId(fieldName)}
        >
            <Controller
                name={fieldName}
                rules={getFieldRules(rowField, required)}
                render={({ field }) => <ImageBufferField field_={rowField} field={field} disabled={disabled} />}
            />
        </FieldRow>
    )
}

export default ImageBufferFieldDefault
